import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Send, Clipboard, Edit2, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Message, AgentStep, DocumentPreview as DocumentPreviewType, MemoryStep } from '../../types';
import { MemorySteps } from '../memory/MemorySteps';
import { StreamingResponse } from './StreamingResponse';
import { AgenticProcessView } from './AgenticProcessView';
import { ReasoningDisplay } from './ReasoningDisplay';
import { DocumentPreview } from '../DocumentPreview';
import { WebSearchSources } from './WebSearchSources';
import { EmailCarousel } from '../email/EmailCarousel';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StreamingText } from "./StreamingText";
import { 
  Bot, MessageSquare, Sparkles, ArrowRight, 
  Search, FileText, Mail, Calendar, Users, 
  Zap, Brain, Globe, Lightbulb
} from 'lucide-react';
import { ChatInput } from './ChatInput';

interface ChatMessagesProps {
  messages: Message[];
  activeSessionId?: string;
  isStreamingResponse?: boolean;
  streamingSteps?: AgentStep[];
  streamingContent?: string;
  memorySteps?: MemoryStep[];
  showMemorySteps?: boolean;
  expandedReasoning?: Set<string>;
  onToggleReasoning?: (messageId: string) => void;
  onDocumentExpand?: (document: DocumentPreviewType) => void;
  onSendMessage?: (message: string) => void;
  onShowEmailComposer?: () => void;
  onAIModifyEmail?: (email: string) => void;
  onContinueFromMemory?: (originalMessage: string) => void;
  onStartNewChatWithMessage?: (message: string) => void;
  onOpenDocumentCanvas?: (content: string, title: string, metadata?: any) => void;
  mcpServices?: Record<string, boolean>;
  onUpdateMcpServices?: (services: Record<string, boolean>) => void;
}

function WelcomeLanding({ 
  onSendMessage, 
  mcpServices = {}, 
  onUpdateMcpServices 
}: { 
  onSendMessage?: (message: string) => void;
  mcpServices?: Record<string, boolean>;
  onUpdateMcpServices?: (services: Record<string, boolean>) => void;
}) {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="max-w-4xl w-full mx-auto text-center px-8">
        {/* Logo/Title - More elegant like Grok */}
        <div className="mb-16">
          <h1 className="text-6xl font-light text-white/90 tracking-tight">
            468
          </h1>
        </div>

        {/* Central Chat Input */}
        <div className="mb-8">
          <ChatInput
            onSendMessage={onSendMessage || (() => {})}
            isTyping={false}
            disabled={false}
            mcpServices={mcpServices}
            onUpdateMcpServices={onUpdateMcpServices}
          />
        </div>

        {/* Quick suggestions */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {[
            "Search my emails",
            "Analyze documents", 
            "Check my schedule",
            "Help me write"
          ].map((suggestion) => (
            <button
              key={suggestion}
              className="px-4 py-2 bg-slate-800/30 hover:bg-slate-800/50 border border-slate-700/20 hover:border-slate-600/30 rounded-full text-sm text-slate-400 hover:text-slate-200 transition-all"
              onClick={() => {
                if (onSendMessage) {
                  onSendMessage(suggestion);
                }
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>

        {/* Footer hints */}
        <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <Users className="w-3 h-3" />
            <span>Multi-account support</span>
          </div>
          <div className="flex items-center gap-1">
            <Brain className="w-3 h-3" />
            <span>AI-powered search</span>
          </div>
          <div className="flex items-center gap-1">
            <Globe className="w-3 h-3" />
            <span>Web search enabled</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ChatMessages({ 
  messages = [],
  activeSessionId,
  isStreamingResponse = false,
  streamingSteps = [],
  streamingContent = "",
  memorySteps = [],
  showMemorySteps = false,
  expandedReasoning = new Set(),
  onToggleReasoning,
  onDocumentExpand,
  onSendMessage,
  onShowEmailComposer,
  onAIModifyEmail,
  onContinueFromMemory,
  onStartNewChatWithMessage,
  onOpenDocumentCanvas,
  mcpServices = {},
  onUpdateMcpServices
}: ChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousMessagesLength = useRef(messages.length);
  const userHasScrolled = useRef(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [copiedForId, setCopiedForId] = useState<string | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    userHasScrolled.current = false;
  };

  const scrollToBottomImmediate = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    userHasScrolled.current = false;
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      userHasScrolled.current = !isNearBottom;
      setShowScrollToTop(scrollTop > 200); // Show scroll-to-top if scrolled more than 200px
    }
  };

  useEffect(() => {
    // Auto-scroll when new messages arrive or content changes
    const shouldAutoScroll = !userHasScrolled.current || messages.length > previousMessagesLength.current;
    
    if (shouldAutoScroll) {
      // Use immediate scroll for new messages, smooth for content updates
      if (messages.length > previousMessagesLength.current) {
        scrollToBottomImmediate();
      } else {
        scrollToBottom();
      }
    }
    
    previousMessagesLength.current = messages.length;
  }, [messages, streamingContent]);

  // Also auto-scroll when streaming starts
  useEffect(() => {
    if (isStreamingResponse && !userHasScrolled.current) {
      scrollToBottom();
    }
  }, [isStreamingResponse]);

  // Show welcome landing when no active session AND no messages
  if (messages.length === 0 && !isStreamingResponse) {
    return (
      <WelcomeLanding 
        onSendMessage={onSendMessage} 
        mcpServices={mcpServices}
        onUpdateMcpServices={onUpdateMcpServices}
      />
    );
  }

  const handleCopy = async (text: string, idForFeedback: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedForId(idForFeedback);
      setTimeout(() => setCopiedForId(null), 1200);
    } catch (e) {
      console.error('Failed to copy text');
    }
  };

  const startEditUserMessage = (message: Message) => {
    setEditingMessageId(message.id);
    setEditValue(message.content || '');
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditValue('');
  };

  const submitEdit = () => {
    const text = editValue.trim();
    if (!text) return;
    if (onStartNewChatWithMessage) {
      onStartNewChatWithMessage(text);
    } else if (onSendMessage) {
      onSendMessage(text);
    }
    setEditingMessageId(null);
    setEditValue('');
  };

  const renderMessage = (message: Message) => {
    const isUser = message.role === 'user';
    
    return (
      <div key={message.id} className={`mb-8 ${isUser ? 'flex justify-end' : ''}`}>
        {isUser ? (
          /* User message - subtle styling with smaller text */
          <div className="max-w-2xl">
            {/* File attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mb-2 space-y-2">
                {message.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="bg-slate-700/50 px-3 py-2 rounded-lg border border-slate-600/50 flex items-center gap-3"
                  >
                    <div className="flex-shrink-0">
                      {attachment.type?.includes('pdf') ? (
                        <FileText className="w-4 h-4 text-red-400" />
                      ) : attachment.type?.includes('image') ? (
                        <img 
                          src={attachment.data} 
                          alt={attachment.name}
                          className="w-8 h-8 object-cover rounded"
                        />
                      ) : (
                        <FileText className="w-4 h-4 text-blue-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-200 text-xs font-medium truncate">
                        {attachment.name}
                      </p>
                      <p className="text-slate-400 text-xs">
                        {attachment.size ? `${(attachment.size / 1024 / 1024).toFixed(1)} MB` : 'Unknown size'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="bg-slate-800/60 px-4 py-3" 
                 style={{
                   borderRadius: '16px 16px 4px 16px',
                   boxShadow: `
                     0 0 0 1px rgba(255, 255, 255, 0.03),
                     0 1px 2px rgba(0, 0, 0, 0.2)
                   `
                 }}>
              {editingMessageId === message.id ? (
                <div>
                  <div className="bg-slate-800/70 border border-slate-700/40 rounded-xl p-3">
                    <textarea
                      className="w-full bg-transparent outline-none resize-none text-slate-100 text-sm leading-relaxed placeholder-slate-400"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          e.preventDefault();
                          submitEdit();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      rows={3}
                      placeholder="Edit your message..."
                    />
                    <div className="mt-2 flex gap-2 justify-end">
                      <button onClick={cancelEdit} className="px-3 py-1.5 rounded-md text-slate-300 hover:text-white hover:bg-slate-700/40 transition-colors">
                        <X className="w-4 h-4 inline mr-1" />Cancel
                      </button>
                      <button onClick={submitEdit} className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                        <Check className="w-4 h-4 inline mr-1" />Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-200 text-sm leading-relaxed">
                  {message.content}
                </p>
              )}
            </div>

            {/* User message actions */}
            <div className="mt-2 flex items-center gap-3 justify-end text-xs text-slate-400">
              <button onClick={() => handleCopy(message.content, message.id)} className={`hover:text-slate-200 ${copiedForId===message.id ? 'text-green-400' : ''}`}>
                <Clipboard className="w-3.5 h-3.5 inline mr-1" />{copiedForId===message.id ? 'Copied' : 'Copy'}
              </button>
              {editingMessageId !== message.id && (
                <button onClick={() => startEditUserMessage(message)} className="hover:text-slate-200">
                  <Edit2 className="w-3.5 h-3.5 inline mr-1" />Edit
                </button>
              )}
            </div>
          </div>
        ) : (
          /* AI response - positioned to the left with markdown support */
          <div className="mr-16">
            {/* Memory Response - Show sources in canvas, content in chat */}
            {message.type === 'memory_response' && message.memoryData && (
              <div className="space-y-2">
                <div className="text-xs text-slate-400 mb-2">
                  📚 Based on previous conversations ({Math.round(message.memoryData.completeness * 100)}% confidence)
                </div>
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Match the styling of normal agent responses
                    p: ({ children }) => <p className="text-white leading-relaxed mb-4 last:mb-0">{children}</p>,
                    h1: ({ children }) => <h1 className="text-white text-2xl font-bold mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-white text-xl font-semibold mb-3">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-white text-lg font-medium mb-2">{children}</h3>,
                    ul: ({ children }) => <ul className="text-white list-disc list-inside mb-4 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="text-white list-decimal list-inside mb-4 space-y-1">{children}</ol>,
                    li: ({ children, ...props }) => <li className="text-white" {...props}>{children}</li>,
                    blockquote: ({ children }) => <blockquote className="border-l-4 border-slate-600 pl-4 italic text-slate-300 mb-4">{children}</blockquote>,
                    code: ({ children, className }) => {
                      const isInline = !className;
                      return isInline ? (
                        <code className="bg-slate-800 text-slate-200 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
                      ) : (
                        <code className="block bg-slate-800 text-slate-200 p-4 rounded-lg text-sm font-mono overflow-x-auto">{children}</code>
                      );
                    },
                    pre: ({ children }) => <pre className="bg-slate-800 rounded-lg mb-4 overflow-hidden">{children}</pre>,
                    a: ({ children, href }) => <a href={href} className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                    strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="text-slate-200 italic">{children}</em>,
                    table: ({ children }) => <table className="border-collapse border border-slate-600 mb-4">{children}</table>,
                    th: ({ children, ...props }) => <th className="border border-slate-600 px-3 py-2 bg-slate-800 text-white font-semibold" {...props}>{children}</th>,
                    td: ({ children, ...props }) => <td className="border border-slate-600 px-3 py-2 text-white" {...props}>{children}</td>,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            )}

            {/* Agent Process View - Show if message has steps or is a tool-using response */}
            {(message.steps && message.steps.length > 0) && (
              <div className="mb-6">
                <AgenticProcessView
                  steps={message.steps}
                  memorySteps={[]} // Memory steps are shown in streaming, not in completed messages
                  isComplete={true}
                />
              </div>
            )}

            {/* Message content with markdown support - Skip for memory responses as they're handled separately */}
            {message.type !== 'memory_response' && (
            <div className="text-white text-base leading-relaxed mb-6 prose prose-invert max-w-none">
              {/* Check if content is too long and show canvas option */}
              {message.content.length > 5000 ? (
                <div className="space-y-4">
                  {/* Show truncated content */}
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ children }) => <p className="text-white leading-relaxed mb-4 last:mb-0">{children}</p>,
                      h1: ({ children }) => <h1 className="text-white text-2xl font-bold mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-white text-xl font-semibold mb-3">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-white text-lg font-medium mb-2">{children}</h3>,
                    ul: ({ children }) => <ul className="text-white list-disc list-inside mb-4 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="text-white list-decimal list-inside mb-4 space-y-1">{children}</ol>,
                    li: ({ children, ...props }) => <li className="text-white" {...props}>{children}</li>,
                    blockquote: ({ children }) => <blockquote className="border-l-4 border-slate-600 pl-4 italic text-slate-300 mb-4">{children}</blockquote>,
                    }}
                  >
                    {message.content.substring(0, 2000) + '...'}
                  </ReactMarkdown>
                  
                  {/* Canvas display button */}
                  <div className="bg-slate-800/50 border border-slate-600/50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-white font-medium">Long content detected</p>
                        <p className="text-slate-400 text-sm">
                          {message.content.length.toLocaleString()} characters • Click to view in canvas
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onOpenDocumentCanvas?.(
                        message.content,
                        `AI Response - ${new Date(message.timestamp).toLocaleString()}`,
                        { 
                          wordCount: message.content.split(/\s+/).length,
                          extractionMethod: 'AI Generated',
                          fileType: 'text'
                        }
                      )}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      View in Canvas
                    </button>
                  </div>
                </div>
              ) : (
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom styling for markdown elements
                    p: ({ children }) => <p className="text-white leading-relaxed mb-4 last:mb-0">{children}</p>,
                    h1: ({ children }) => <h1 className="text-white text-2xl font-bold mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-white text-xl font-semibold mb-3">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-white text-lg font-medium mb-2">{children}</h3>,
                    ul: ({ children }) => <ul className="text-white list-disc list-inside mb-4 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="text-white list-decimal list-inside mb-4 space-y-1">{children}</ol>,
                    li: ({ children, ...props }) => <li className="text-white" {...props}>{children}</li>,
                    blockquote: ({ children }) => <blockquote className="border-l-4 border-slate-600 pl-4 italic text-slate-300 mb-4">{children}</blockquote>,
                  code: ({ children, className }) => {
                    const isInline = !className;
                    return isInline ? (
                      <code className="bg-slate-800 text-slate-200 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
                    ) : (
                      <code className="block bg-slate-800 text-slate-200 p-4 rounded-lg text-sm font-mono overflow-x-auto">{children}</code>
                    );
                  },
                  pre: ({ children }) => <pre className="bg-slate-800 rounded-lg mb-4 overflow-hidden">{children}</pre>,
                  a: ({ children, href }) => <a href={href} className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                  em: ({ children }) => <em className="text-slate-200 italic">{children}</em>,
                  table: ({ children }) => <table className="border-collapse border border-slate-600 mb-4">{children}</table>,
                  th: ({ children, ...props }) => <th className="border border-slate-600 px-3 py-2 bg-slate-800 text-white font-semibold" {...props}>{children}</th>,
                  td: ({ children, ...props }) => <td className="border border-slate-600 px-3 py-2 text-white" {...props}>{children}</td>,
                }}
              >
                {message.content}
              </ReactMarkdown>
              )}
            </div>
            )}

            {/* Documents are now displayed only in the side panel */}

            {/* Assistant message actions */}
            <div className="-mt-4 mb-2 flex items-center gap-3 text-xs text-slate-400">
              <button onClick={() => handleCopy(message.content, message.id)} className={`hover:text-slate-200 ${copiedForId===message.id ? 'text-green-400' : ''}`}>
                <Clipboard className="w-3.5 h-3.5 inline mr-1" />{copiedForId===message.id ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* Messages Container - Fixed height with proper scrolling */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto max-w-4xl mx-auto px-6 pt-12 pb-24 messages-container"
        style={{ 
          height: '100%',
          maxHeight: '100%',
          minHeight: 0 // Important for flex children
        }}
      >
        {/* Messages - Always show when we have an active session or messages */}
        {messages.map((message) => renderMessage(message))}
        
        {/* Active Agent Process - Show during streaming */}
        {(isStreamingResponse && (streamingSteps.length > 0 || memorySteps.length > 0)) && (
          <div className="mb-8 mr-16">
            <AgenticProcessView
              steps={streamingSteps}
              memorySteps={memorySteps}
              isComplete={false}
            />
          </div>
        )}
        
        {/* Streaming response */}
        {isStreamingResponse && streamingContent && (
          <div className="mb-8 mr-16">
            <div className="text-white text-base leading-relaxed prose prose-invert max-w-none">
              <StreamingText 
                content={streamingContent}
                isStreaming={isStreamingResponse}
                className="prose prose-invert max-w-none"
                enableMarkdown={true}
              />
            </div>
          </div>
        )}
        
        {/* Auto-scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Scroll to top button */}
      {showScrollToTop && (
        <button
          onClick={() => {
            messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="fixed right-8 bottom-24 bg-slate-700 hover:bg-slate-600 text-white p-3 rounded-full shadow-lg transition-all duration-200 z-10"
          style={{ marginRight: 'max(0px, calc((100vw - 1024px) / 2))' }}
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
